import React, { useState, useEffect } from 'react';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { getThemeKey, applyTheme, saveThemePreference } from '../../hooks/useTheme';

const AppearanceSettings = ({ onClose, user }) => {
  const [theme, setTheme] = useState('light');
  const [saving, setSaving] = useState(false);
  const themeKey = getThemeKey(user);

  // Dark mode is mocked for now — the rider app stays light-only.
  // We still persist "light" so the account has a clean, valid value.
  useEffect(() => {
    if (!user?.id) return;
    setTheme('light');
    if (themeKey) {
      try { localStorage.setItem(themeKey, 'light'); } catch (_) { /* noop */ }
    }
    applyTheme('light');
  }, [user?.id, themeKey]);

  const handleThemeChange = async (selectedTheme) => {
    // Only light is available today; dark/device are "coming soon" placeholders.
    if (selectedTheme !== 'light') return;
    setTheme('light');
    setSaving(true);
    await saveThemePreference(user, 'light');
    setSaving(false);
  };

  const themeOptions = [
    {
      id: 'light',
      label: 'Light Mode',
      description: 'Always use light theme',
      icon: Sun,
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      comingSoon: false
    },
    {
      id: 'dark',
      label: 'Dark Mode',
      description: 'Coming soon',
      icon: Moon,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      comingSoon: true
    },
    {
      id: 'device',
      label: 'Device Settings',
      description: 'Coming soon',
      icon: Monitor,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      comingSoon: true
    }
  ];

  return (
    <div className="fixed inset-0 z-[60] h-full flex flex-col bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-colors">
        <button
          onClick={onClose}
          className="flex items-center text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
        >
          <span className="text-xl mr-2">‹</span>
          <span className="font-medium">Back</span>
        </button>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Appearance</h2>
        <div className="w-16 text-right">
          {saving && <span className="text-[11px] text-gray-500" data-testid="appearance-saving">Saving…</span>}
        </div> {/* Spacer for centering */}
      </div>

      {/* Theme Options */}
      <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 transition-colors">
        <div className="px-4 py-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Choose how Yachangu looks to you. Select a single theme, or sync with your device and automatically switch between day and night themes.
          </p>

          <div className="space-y-2">
            {themeOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = theme === option.id;
              const disabled = option.comingSoon;

              return (
                <button
                  key={option.id}
                  onClick={() => handleThemeChange(option.id)}
                  disabled={disabled}
                  data-testid={`appearance-theme-${option.id}`}
                  className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                    isSelected
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center space-x-4">
                    {/* Icon */}
                    <div className={`w-12 h-12 ${option.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-6 h-6 ${option.iconColor}`} />
                    </div>
                    
                    {/* Text */}
                    <div className="text-left">
                      <div className="font-semibold text-gray-900 flex items-center gap-2">
                        {option.label}
                        {option.comingSoon && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                            Coming soon
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {option.description}
                      </div>
                    </div>
                  </div>

                  {/* Check Icon */}
                  {isSelected && (
                    <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Helper note */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              <span className="font-semibold">Tip:</span> Your appearance choice is saved to your account only. Other users on this device keep their own theme.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppearanceSettings;
